import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { ResourceTypeCodes } from 'src/app/core/_enums/resource.type.codes';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Family } from 'src/app/core/_models/family';
import { AuthService } from 'src/app/core/_services/auth.service';
import { FamilyService } from 'src/app/core/_services/family.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { DialogFamilyComponent } from '../dialog-family/dialog-family.component';

interface ItemConfig {
  displayName: string;
  url: string;
  showBadge?: boolean;
  itemType?: any;
  state?: any;
}

export interface RessourceListConfig {
  header: {
    filterActionList?(filterValue?: GroupBy): {
      label: string;
      onClick: () => void;
    }[];
    title: string;
    actions: {
      label: string;
      onClick: () => void;
    }[];
    filters: {
      label: string;
      value: string;
    }[];
    description?: string;
    onNameChanged?: (target: any) => void;
    onFilterChange?: (filter: string) => void;
    defaultFilter?: GroupBy;
  };
  dataSource: MatTreeNestedDataSource<any>;
  getItemConfig?: (node: any) => ItemConfig;
  onShare?: (selectedItems: any[]) => void;
  onDelete?: (selectedItems: any[], groupBy?: GroupBy) => void;
  onFamilyEdit?: () => void;
  getEditableFamilyId?: (node: any) => string;
  addFamilyType?: ResourceTypeCodes;
}

@Component({
  selector: 'ressource-list',
  templateUrl: './ressource-list.component.html',
  styleUrls: ['./ressource-list.component.scss']
})
export class RessourceListComponent implements OnInit, OnDestroy, OnChanges {
  @Input() config!: RessourceListConfig;

  treeControl = new NestedTreeControl<any>((node) => node.value, { trackBy: (node) => this.getRessourceId(node) });
  selectedFilterValue: string = '';
  refItems: any[] = [];
  selectedRessourceIds: Set<string | number> = new Set();
  manageFamily: boolean = false;
  perimeterId!: string;
  get canShare(): boolean {
    return typeof this.config?.onShare === 'function';
  }
  get canDelete(): boolean {
    return typeof this.config?.onDelete === 'function';
  }
  get hasSelectionActions(): boolean {
    return this.canShare || this.canDelete;
  }
  private searchFilter = new Subject<string>();
  private currentSearch = '';
  private destroy$ = new Subject<void>();

  constructor(
    private familyService: FamilyService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
    this.manageFamily = this.authService.accessesValues.includes(FunctionalityCode.ManageFamily);
    this.perimeterId = this.authService.roleSubject.value.perimeterId;
    this.searchFilter.pipe(debounceTime(50), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.performSearch(value);
    });
    this.expandDefaultNodes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].isFirstChange()) {
      this.initializeComponent();
      this.selectedRessourceIds.clear();
      if (this.currentSearch.length >= 2) this.performSearch(this.currentSearch);
    }
    this.expandDefaultNodes();
  }

  private expandDefaultNodes(nodes: any[] = this.config?.dataSource?.data || []): void {
    nodes.forEach((node) => {
      if (node?.expanded) {
        this.treeControl.expand(node);
      }
      if (Array.isArray(node?.value)) {
        this.expandDefaultNodes(node.value);
      }
    });
  }

  private initializeComponent(): void {
    if (this.config?.header?.defaultFilter) {
      this.selectedFilterValue = this.config.header.defaultFilter;
    } else if (this.config?.header?.filters?.length > 0) {
      this.selectedFilterValue = this.config.header.filters[0].value;
    }

    if (this.config?.dataSource?.data && Array.isArray(this.config.dataSource.data)) {
      this.refItems = [...this.config.dataSource.data];
    } else {
      this.refItems = [];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  resetToOriginalData(): void {
    if (this.refItems && this.refItems.length > 0) {
      this.config.dataSource.data = [...this.refItems];
      this.treeControl.dataNodes = [...this.refItems];
    }
  }

  getRessourceId(ressource: any): string {
    return ressource.resourceId || ressource.key || ressource.agencyId || ressource.industryId || ressource.workInspectionId || ressource.id || ressource.scenarioId || ressource.userId || ressource.memberId || ressource.displayName;
  }

  onRessourceSelectionChange(ressource: any): void {
    if (!this.hasSelectionActions) {
      return;
    }
    const ressourceId = this.getRessourceId(ressource);
    if (this.selectedRessourceIds.has(ressourceId)) {
      this.selectedRessourceIds.delete(ressourceId);
    } else {
      this.selectedRessourceIds.add(ressourceId);
    }
  }

  isRessourceSelected(ressource: any): boolean {
    const ressourceId = this.getRessourceId(ressource);
    return this.selectedRessourceIds.has(ressourceId);
  }

  getSelectedItems(): any[] {
    if (!this.hasSelectionActions) {
      return [];
    }
    return this.collectSelectedNodes(this.config.dataSource.data);
  }

  getSelectedRessourcesCount(): number {
    if (!this.hasSelectionActions) {
      return 0;
    }
    return this.selectedRessourceIds.size;
  }

  onShareClick(): void {
    if (this.config.onShare) {
      this.config.onShare(this.getSelectedItems());
    }
  }

  onDeleteClick(): void {
    if (this.config.onDelete) {
      this.config.onDelete?.(this.getSelectedItems());
    }
  }

  getItemConfig(node: any): ItemConfig | null {
    if (!this.config.getItemConfig) {
      return null;
    }

    const itemConfig = this.config.getItemConfig(node);
    return { ...itemConfig, state: { ...itemConfig.state, groupBy: this.selectedFilterValue } };
  }

  private isProjectItem(itemConfig: ItemConfig | null): boolean {
    return itemConfig?.itemType === 'project-list';
  }

  private isWorkInspectionItem(itemConfig: ItemConfig | null): boolean {
    return itemConfig?.itemType === 'work-inspection-item';
  }

  onFilterChange(filterValue: string): void {
    if (this.config.header?.onFilterChange) {
      this.config.header.onFilterChange(filterValue);
    }
  }

  onNameChanged(target: string = '') {
    this.searchFilter.next(target);
  }

  private normalizeString(str: string | null | undefined): string {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  private performSearch(target: string): void {
    this.currentSearch = target;
    if (!this.refItems || this.refItems.length === 0) {
      this.refItems = [...this.config.dataSource.data];
    }

    if (target.length < 2) {
      this.resetToOriginalData();
      return;
    }

    const normalizedTarget = this.normalizeString(target);
    let filteredData: any[] = [];

    const firstItem = this.refItems[0];
    if (!firstItem) return;

    if (firstItem.displayName || firstItem.code) {
      filteredData = this.refItems.filter((item) => this.normalizeString(item.displayName).includes(normalizedTarget) || this.normalizeString(item.code).includes(normalizedTarget));
    } else if (firstItem.adherentDisplayName && firstItem.users) {
      let filteredParents = this.refItems.filter(
        (c) => this.normalizeString(c.adherentDisplayName).includes(normalizedTarget) || (c.users && c.users.some((x: { userDisplayName: string }) => this.normalizeString(x.userDisplayName).includes(normalizedTarget)))
      );

      for (const element of filteredParents) {
        let filteredChildren = element.users.filter((v: { userDisplayName: string }) => this.normalizeString(v.userDisplayName).includes(normalizedTarget));
        const item = {
          ...element,
          users: filteredChildren
        };
        filteredData.push(item);
      }
    } else if (firstItem.value) {
      const firstChild = firstItem.value[0];

      if (firstChild && (firstChild.value || firstChild.displayName)) {
        let filteredParents = this.refItems.filter(
          (c) =>
            this.normalizeString(c.key).includes(normalizedTarget) ||
            c.value.some(
              (x: any) =>
                this.normalizeString(x.displayName).includes(normalizedTarget) ||
                this.normalizeString(x.code).includes(normalizedTarget) ||
                this.normalizeString(x.key).includes(normalizedTarget) ||
                (x.value && x.value.some((y: any) => this.normalizeString(y.displayName).includes(normalizedTarget) || this.normalizeString(y.code).includes(normalizedTarget)))
            )
        );

        filteredParents.forEach((fP: { value: any[]; key: any }) => {
          let filteredChildrenValue: any[] = [];

          let filteredChildren = fP.value.filter(
            (v: any) =>
              this.normalizeString(v.key).includes(normalizedTarget) ||
              this.normalizeString(v.displayName).includes(normalizedTarget) ||
              this.normalizeString(v.code).includes(normalizedTarget) ||
              (v.value && v.value.some((y: any) => this.normalizeString(y.displayName).includes(normalizedTarget) || this.normalizeString(y.code).includes(normalizedTarget)))
          );

          filteredChildren.forEach((fC: any) => {
            if (fC.value && Array.isArray(fC.value)) {
              let filteredChildrenNext = fC.value.filter((z: any) => this.normalizeString(z.displayName).includes(normalizedTarget) || this.normalizeString(z.code).includes(normalizedTarget));

              let itemChildren = {
                ...fC,
                value: filteredChildrenNext
              };

              if (filteredChildrenNext.length > 0 || this.normalizeString(fC.key).includes(normalizedTarget)) {
                filteredChildrenValue.push(itemChildren);
              }
            } else {
              filteredChildrenValue.push(fC);
            }
          });

          if (filteredChildrenValue.length > 0 || this.normalizeString(fP.key).includes(normalizedTarget)) {
            let item = {
              ...fP,
              value: filteredChildrenValue.length > 0 ? filteredChildrenValue : fP.value
            };
            filteredData.push(item);
          }
        });
      } else {
        let filteredParents = this.refItems.filter(
          (c) =>
            this.normalizeString(c.key).includes(normalizedTarget) ||
            c.value.some((x: { displayName: string }) => this.normalizeString(x?.displayName).includes(normalizedTarget)) ||
            c.value.some((x: { code: string }) => this.normalizeString(x?.code).includes(normalizedTarget))
        );

        for (const parent of filteredParents) {
          let filteredChildren = parent.value.filter((v: { displayName: string; code: string }) => this.normalizeString(v?.displayName).includes(normalizedTarget) || this.normalizeString(v?.code).includes(normalizedTarget));
          var itemWithValue = {
            ...parent,
            value: filteredChildren
          };
          filteredData.push(itemWithValue);
        }
      }
    }

    this.config.dataSource.data = filteredData;
    this.treeControl.dataNodes = filteredData;
    this.treeControl.expandAll();
  }

  hasChild = (_: number, node: any) => (!!node.value && node.value.length > 0) || node.type;

  async addNewFamily() {
    if (!this.manageFamily || !this.config.addFamilyType) return;
    const dialogRef = this.dialog.open(DialogFamilyComponent, {
      data: { family: {} }
    });

    dialogRef.afterClosed().subscribe(async (newFamily) => {
      if (newFamily) {
        try {
          let resp = await this.familyService.addFamily(newFamily, this.config.addFamilyType!);
          if (resp) {
            this.snackbarService.open(SnackbarTypes.add);
          } else {
            this.snackbarService.open(SnackbarTypes.error);
          }
        } catch (error) {
          this.snackbarService.open(SnackbarTypes.error);
        }
      }
    });
  }

  async editFamily(node: any) {
    if (!this.manageFamily || !this.config.getEditableFamilyId) return;
    let family: Family | undefined;
    try {
      family = await this.familyService.getFamily(this.config.getEditableFamilyId(node));
    } catch (error) {
      this.snackbarService.open(SnackbarTypes.error);
      return;
    }
    if (!family) {
      this.snackbarService.open(SnackbarTypes.error);
      return;
    }
    const dialogRef = this.dialog.open(DialogFamilyComponent, {
      data: { family }
    });

    dialogRef.afterClosed().subscribe(async (editedFamily) => {
      if (editedFamily) {
        try {
          let resp = await this.familyService.editFamily(editedFamily);
          if (resp) {
            // this.getAll(GroupBy.family);
            this.config.onFamilyEdit?.();
            this.snackbarService.open(SnackbarTypes.update);
          } else {
            this.snackbarService.open(SnackbarTypes.error);
          }
        } catch (error) {
          this.snackbarService.open(SnackbarTypes.error);
        }
      }
    });
  }

  private collectSelectedNodes(nodes: any[]): any[] {
    if (!Array.isArray(nodes) || !nodes.length) {
      return [];
    }
    const selected: any[] = [];
    nodes.forEach((node) => {
      if (!node) {
        return;
      }
      const ressourceId = this.getRessourceId(node);
      if (this.selectedRessourceIds.has(ressourceId)) {
        selected.push(node);
      }
      const children = this.getNodeChildren(node);
      if (children.length) {
        selected.push(...this.collectSelectedNodes(children));
      }
    });
    return selected;
  }

  private getNodeChildren(node: any): any[] {
    if (!node) {
      return [];
    }
    if (Array.isArray(node.value)) {
      return node.value;
    }
    if (Array.isArray(node.users)) {
      return node.users;
    }
    return [];
  }

  isItemDeletable(item: any, itemConfig?: ItemConfig | null): boolean {
    if (!item) {
      return false;
    }

    if (itemConfig && this.isProjectItem(itemConfig)) {
      return true;
    }

    if (itemConfig && this.isWorkInspectionItem(itemConfig)) {
      return this.authService.accessesValues.includes(FunctionalityCode.ManageWorkInspection);
    }

    let manageProducts = this.authService.accessesValues.includes(FunctionalityCode.ManageProducts);
    if (!manageProducts) {
      return false;
    }
    return !item.ruade;
  }
}
